/**
 * ContractExpertWelcome - Welcome/onboarding modal for Contract Expert
 * 
 * Shows once per user (persisted server-side) with important disclaimers
 * and getting started information.
 */

import { useState, useEffect } from "react";
import apiClient from "../../utils/apiClient";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  MessageSquare,
  FileText,
  Shield,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

export default function ContractExpertWelcome({ children }) {
  const [showWelcome, setShowWelcome] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    checkWelcomeStatus();
  }, []);

  const checkWelcomeStatus = async () => {
    try {
      // Check for session dismissal flag first
      const dismissedSession = sessionStorage.getItem("agent_terms_dismissed_session");
      if (dismissedSession) {
        setShowWelcome(false);
        setLoading(false);
        return;
      }
      
      // Check if user has already seen and accepted the Contract Expert welcome
      const res = await apiClient.get("/agent/workspace/me");
      const workspace = res.data?.workspace;
      
      // Show welcome if user hasn't accepted Contract Expert terms yet
      const hasAccepted = workspace?.agent_terms_accepted === true;
      setShowWelcome(!hasAccepted);
    } catch (err) {
      // If we can't check, don't block the user
      setShowWelcome(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!agreedToTerms) {
      return;
    }
    
    setAccepting(true);
    try {
      await apiClient.post("/agent/workspace/accept-terms");
      setShowWelcome(false);
    } catch (err) {
      console.error("Failed to accept terms:", err);
      // Store a session flag so we don't flash the modal again on this session
      sessionStorage.setItem("agent_terms_dismissed_session", "true");
      setShowWelcome(false);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    // Check session storage for dismissed-this-session flag
    const dismissedSession = sessionStorage.getItem("agent_terms_dismissed_session");
    if (dismissedSession) {
      return children;
    }
    return children; // Don't block while loading
  }

  return (
    <>
      <Dialog open={showWelcome} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-lg"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          data-testid="contract-expert-welcome-modal"
        >
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-primary" />
              </div>
              <DialogTitle className="text-xl">
                Welcome to Contract Expert
              </DialogTitle>
            </div>
            <DialogDescription className="text-left">
              Your 24/7 AI-powered labor relations advisor
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* What you can do */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">What you can do:</h4>
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Upload collective agreements, contracts, and policies</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Ask questions and get answers with exact citations</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Access via web chat, Telegram, or WhatsApp</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Send voice notes for hands-free queries</span>
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm">
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Important Notice
                  </p>
                  <ul className="space-y-1 text-amber-700 dark:text-amber-300">
                    <li>• This is an <strong>educational tool</strong>, not legal advice</li>
                    <li>• Answers are based only on your uploaded documents</li>
                    <li>• Always consult HR, labor relations, or legal counsel before taking action</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Privacy */}
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>
                Your documents are private and never shared. They are used only to answer your questions.
              </p>
            </div>

            {/* Agreement checkbox */}
            <div className="flex items-start gap-3 pt-2 border-t">
              <Checkbox
                id="agent-terms"
                checked={agreedToTerms}
                onCheckedChange={setAgreedToTerms}
                data-testid="agent-terms-checkbox"
              />
              <label
                htmlFor="agent-terms"
                className="text-sm cursor-pointer select-none"
              >
                I understand that Contract Expert provides educational information only, 
                not legal advice. I will consult appropriate professionals before taking 
                any action based on the information provided.
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={handleAccept}
              disabled={!agreedToTerms || accepting}
              className="w-full sm:w-auto"
              data-testid="accept-agent-terms-btn"
            >
              {accepting ? "Starting..." : "Get Started"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {children}
    </>
  );
}
